import { useQuery } from '@tanstack/react-query';
import { requestApiGetConjunctions } from '@/query/conjunctions/useQueryCdm';

export interface DCAStats {
  dca1km: number;
  dca5km: number;
  dca10km: number;
}

const COLLECTION = 'recent';

// A cheap count request: limit=1 avoids downloading rows — we only read totalCount.
const countParams = (dcaUpper: number) => ({
  option: 'simple',
  log: 'www',
  limit: 1,
  page: 0,
  dcaUpper,
});

export const fetchDCAStats = async (): Promise<DCAStats> => {
  const results = await Promise.allSettled([
    requestApiGetConjunctions(countParams(1), COLLECTION),
    requestApiGetConjunctions(countParams(5), COLLECTION),
    requestApiGetConjunctions(countParams(10), COLLECTION),
  ]);

  const getCount = (result: PromiseSettledResult<{ totalCount?: number }>) =>
    result.status === 'fulfilled' ? (result.value?.totalCount ?? 0) : 0;

  return {
    dca1km: getCount(results[0]),
    dca5km: getCount(results[1]),
    dca10km: getCount(results[2]),
  };
};

/**
 * Live "DCA < 1km / 5km / 10km" counts for the recent conjunction collection,
 * matching the data shown in the Conjunction Briefing table. Replaces the
 * previously hardcoded placeholder figures.
 */
export const useDCAStats = () => {
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['dca-stats', COLLECTION],
    queryFn: fetchDCAStats,
    refetchOnWindowFocus: false,
  });

  return {
    stats: data ?? null,
    isLoading: isLoading || isFetching,
    error: (error as Error) ?? null,
  };
};
